import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('agrolink_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('agrolink_token');
      window.location.href = '/login';
    }
    // Assinatura bloqueada
    if (error.response?.status === 403 && error.response?.data?.detail === 'subscription_blocked') {
      window.location.href = '/acesso-bloqueado';
    }
    // Manutenção
    if (error.response?.status === 503 && error.response?.data?.detail === 'manutencao') {
      window.location.href = '/manutencao';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
};

// Users
export const usersAPI = {
  list: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Partners
export const partnersAPI = {
  list: () => api.get('/partners'),
  create: (data) => api.post('/partners', data),
  update: (id, data) => api.put(`/partners/${id}`, data),
  delete: (id) => api.delete(`/partners/${id}`),
};

// Cargos e Permissões
export const cargosAPI = {
  list: () => api.get('/cargos'),
  listPaginas: () => api.get('/cargos/paginas'),
  create: (data) => api.post('/cargos', data),
  update: (cargo, data) => api.put(`/cargos/${cargo}`, data),
  delete: (cargo) => api.delete(`/cargos/${cargo}`),
  getMinhasPermissoes: () => api.get('/cargos/minhas-permissoes'),
};

// Clients
export const clientsAPI = {
  list: (search) => api.get('/clients', { params: { search } }),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getHistory: (id) => api.get(`/clients/${id}/history`),
  transferir: (id, novoUsuarioId) => api.put(`/clients/${id}/transferir`, { novo_usuario_id: novoUsuarioId }),
  exportCSV: () => api.get('/clients/export/csv', { responseType: 'blob' }),
  importTemplate: () => api.get('/clients/import/template', { responseType: 'blob' }),
  importCSV: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/clients/import/csv', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Etapas
export const etapasAPI = {
  list: () => api.get('/etapas'),
  listByTipoProjeto: (tipoProjetoId) => api.get(`/etapas/por-projeto/${tipoProjetoId}`),
  create: (data) => api.post('/etapas', data),
  update: (id, data) => api.put(`/etapas/${id}`, data),
  delete: (id) => api.delete(`/etapas/${id}`),
};

// Projects
export const projectsAPI = {
  list: (params) => api.get('/projects', { params }),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  listByClient: (clientId) => api.get(`/projects/by-client/${clientId}`),
  nextStage: (id) => api.put(`/projects/${id}/next-stage`),
  previousStage: (id) => api.put(`/projects/${id}/previous-stage`),
  advanceStage: (id) => api.put(`/projects/${id}/next-stage`),
  archive: (id) => api.put(`/projects/${id}/archive`),
  cancel: (id, data) => api.put(`/projects/${id}/cancel`, data),
  addPendencia: (id, data) => api.post(`/projects/${id}/pendencia`, data),
  resolvePendencia: (id, index, resolucao = '') => api.put(`/projects/${id}/pendencia/${index}/resolve`, { resolucao }),
  suspender: (id, motivo = '') => api.post(`/projects/${id}/suspender`, { motivo }),
  retomar: (id) => api.post(`/projects/${id}/retomar`),
  addObservacao: (id, data) => api.post(`/projects/${id}/observacao`, data),
  updateDocuments: (id, data) => api.put(`/projects/${id}/documents`, data),
  // Laudos (Fase 3)
  listLaudos: (id) => api.get(`/projects/${id}/laudos`),
  addLaudo: (id, data) => api.post(`/projects/${id}/laudos`, data),
  deleteLaudo: (id, laudoId) => api.delete(`/projects/${id}/laudos/${laudoId}`),
  toggleLaudoRealizado: (id, laudoId) => api.put(`/projects/${id}/laudos/${laudoId}/toggle-realizado`),
  // Parcelas / Pagamentos (Fase 4)
  setServicoModo: (id, parcelado) => api.put(`/projects/${id}/servico-modo`, { servico_parcelado: parcelado }),
  listParcelas: (id) => api.get(`/projects/${id}/parcelas`),
  addParcela: (id, data) => api.post(`/projects/${id}/parcelas`, data),
  updateParcela: (id, parcelaId, data) => api.put(`/projects/${id}/parcelas/${parcelaId}`, data),
  toggleParcelaPago: (id, parcelaId) => api.put(`/projects/${id}/parcelas/${parcelaId}/toggle-pago`),
  deleteParcela: (id, parcelaId) => api.delete(`/projects/${id}/parcelas/${parcelaId}`),
};

export const laudosAPI = {
  notifications: () => api.get('/laudos/notifications'),
  byMonth: (year, month) => api.get('/laudos/by-month', { params: { year, month } }),
};

export const pagamentosAPI = {
  notifications: () => api.get('/pagamentos/notifications'),
  byMonth: (year, month) => api.get('/pagamentos/by-month', { params: { year, month } }),
};

// Files
export const filesAPI = {
  list: (clientId) => api.get(`/files/${clientId}`),
  upload: (clientId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/upload/${clientId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (clientId, filename) => api.get(`/files/${clientId}/${filename}`, { responseType: 'blob' }),
  delete: (clientId, filename) => api.delete(`/files/${clientId}/${filename}`),
};

// Alerts
export const alertsAPI = {
  getAll: () => api.get('/alerts/all'),
  check: () => api.get('/alerts'),
  getPropostas: () => api.get('/alerts/propostas'),
  clearPropostaAlert: (id) => api.put(`/alerts/propostas/${id}/clear`),
  clearAllPropostaAlerts: () => api.put('/alerts/propostas/clear-all'),
};

// Propostas
export const propostasAPI = {
  list: (params) => api.get('/propostas', { params }),
  get: (id) => api.get(`/propostas/${id}`),
  create: (data) => api.post('/propostas', data),
  converter: (id) => api.put(`/propostas/${id}/converter`),
  desistir: (id, data) => api.put(`/propostas/${id}/desistir`, data),
  delete: (id) => api.delete(`/propostas/${id}`),
};

// Instituições Financeiras
export const instituicoesAPI = {
  list: () => api.get('/instituicoes-financeiras'),
  listAll: () => api.get('/instituicoes-financeiras/all'),
  create: (data) => api.post('/instituicoes-financeiras', data),
  update: (id, data) => api.put(`/instituicoes-financeiras/${id}`, data),
  delete: (id) => api.delete(`/instituicoes-financeiras/${id}`),
};

// Tipos de Projeto
export const tiposProjetoAPI = {
  list: () => api.get('/tipos-projeto'),
  listAll: () => api.get('/tipos-projeto/all'),
  create: (data) => api.post('/tipos-projeto', data),
  update: (id, data) => api.put(`/tipos-projeto/${id}`, data),
  delete: (id) => api.delete(`/tipos-projeto/${id}`),
};

// Requisitos de Etapa
export const requisitosEtapaAPI = {
  list: (etapaId) => api.get('/requisitos-etapa', { params: { etapa_id: etapaId } }),
  listByTipoProjeto: (tipoProjetoId, etapaId) => api.get(`/requisitos-etapa/por-projeto/${tipoProjetoId}`, { params: { etapa_id: etapaId } }),
  create: (data) => api.post('/requisitos-etapa', data),
  update: (id, data) => api.put(`/requisitos-etapa/${id}`, data),
  delete: (id) => api.delete(`/requisitos-etapa/${id}`),
  seedDefaults: () => api.post('/requisitos-etapa/seed-defaults'),
};

// Marcas de Etapa (campos persistentes que ficam fixos a partir de uma etapa)
export const marcasEtapaAPI = {
  list: (etapaId) => api.get('/marcas-etapa', { params: { etapa_id: etapaId } }),
  listByTipoProjeto: (tipoProjetoId, etapaId) => api.get(`/marcas-etapa/por-projeto/${tipoProjetoId}`, { params: { etapa_id: etapaId } }),
  create: (data) => api.post('/marcas-etapa', data),
  update: (id, data) => api.put(`/marcas-etapa/${id}`, data),
  delete: (id) => api.delete(`/marcas-etapa/${id}`),
  saveProjectMarca: (projectId, marcaId, valor) =>
    api.put(`/projects/${projectId}/marca`, { marca_id: marcaId, valor }),
  migrateLegacy: (marcaId, legacyField, overwrite = false) =>
    api.post(`/marcas-etapa/${marcaId}/migrate`, { legacy_field: legacyField, overwrite }),
};

// Estados e Cidades
export const localizacaoAPI = {
  getEstados: () => api.get('/estados'),
  getCidades: (estadoSigla) => api.get(`/cidades/${estadoSigla}`),
};

// Config
export const configAPI = {
  get: () => api.get('/config'),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/config/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updateCamposExtras: (campos) => api.put('/config/campos-extras', { campos }),
};

// Reports
export const reportsAPI = {
  summary: (params) => api.get('/reports/summary', { params }),
};

// v1.7 — Favoritos de Relatório
export const reportFavoritesAPI = {
  list: (modulo) => api.get('/report-favorites', { params: modulo ? { modulo } : {} }),
  create: (data) => api.post('/report-favorites', data),
  update: (id, data) => api.put(`/report-favorites/${id}`, data),
  delete: (id) => api.delete(`/report-favorites/${id}`),
};

// Dashboard
export const dashboardAPI = {
  stats: () => api.get('/dashboard/stats'),
};

// Empresa Config
// Informações públicas da empresa (nome, logo, páginas ocultas). Buscadas uma vez por carregamento.
let _publicInfo = null;
export const getEmpresaPublicInfo = (forcar = false) => {
  if (!_publicInfo || forcar) {
    _publicInfo = api.get('/empresa/public-info').then((r) => r.data || {}).catch(() => { _publicInfo = null; return {}; });
    // avisa menu e sinos quando a configuração muda (ex.: ocultar Propostas)
    if (forcar) _publicInfo.then((d) => window.dispatchEvent(new CustomEvent('empresa-info', { detail: d })));
  }
  return _publicInfo;
};

export const empresaAPI = {
  getConfig: () => api.get('/empresa/config'),
  updateConfig: (data) => api.put('/empresa/config', data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/empresa/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLogo: () => api.delete('/empresa/logo'),
  getLogoUrl: () => `${API_URL}/api/empresa/logo`,
  updateAdminCredentials: (data) => api.put('/empresa/admin-credentials', data),
};

// Audit Logs
export const logsAPI = {
  list: (params) => api.get('/logs', { params }),
  stats: () => api.get('/logs/stats'),
};

// Manutenção
export const manutencaoAPI = {
  status: () => api.get('/manutencao/status'),
  toggle: (data) => api.put('/manutencao/toggle', data),
};

// Master Only
export const masterAPI = {
  getDataStats: () => api.get('/master/data-stats'),
  resetAllData: () => api.delete('/master/reset-all-data'),
};

// Atividades
export const atividadesAPI = {
  list: (params) => api.get('/atividades', { params }),
  get: (id) => api.get(`/atividades/${id}`),
  create: (data) => api.post('/atividades', data),
  update: (id, data) => api.put(`/atividades/${id}`, data),
  resolver: (id) => api.put(`/atividades/${id}/resolver`),
  reabrir: (id) => api.put(`/atividades/${id}/reabrir`),
  cancelar: (id) => api.put(`/atividades/${id}/cancelar`),
  transferir: (id, novoAtribuidoAId) => api.put(`/atividades/${id}/transferir`, { novo_atribuido_a_id: novoAtribuidoAId }),
  emAndamento: (id) => api.put(`/atividades/${id}/em-andamento`),
  addResposta: (id, texto) => api.post(`/atividades/${id}/resposta`, { texto }),
  marcarLida: (id) => api.put(`/atividades/${id}/marcar-lida`),
  notifications: () => api.get('/atividades/notifications'),
  unreadList: () => api.get('/atividades/unread-list'),
  canCreate: () => api.get('/atividades/can-create'),
  countsByProject: () => api.get('/atividades/counts-por-projeto'),
};

// Módulos extras (add-ons)
export const modulosAPI = {
  ativos: () => api.get('/modulos-ativos'),
};

// Financeiro (Add-on isolado)
export const financeiroAPI = {
  // Dashboard
  dashboard: (params) => api.get('/financeiro/dashboard', { params }),
  // Service Types
  listServiceTypes: () => api.get('/financeiro/service-types'),
  createServiceType: (data) => api.post('/financeiro/service-types', data),
  updateServiceType: (id, data) => api.put(`/financeiro/service-types/${id}`, data),
  deleteServiceType: (id) => api.delete(`/financeiro/service-types/${id}`),
  // Payment Methods
  listPaymentMethods: () => api.get('/financeiro/payment-methods'),
  createPaymentMethod: (data) => api.post('/financeiro/payment-methods', data),
  updatePaymentMethod: (id, data) => api.put(`/financeiro/payment-methods/${id}`, data),
  deletePaymentMethod: (id) => api.delete(`/financeiro/payment-methods/${id}`),
  // Categories
  listCategories: (tipo) => api.get('/financeiro/categories', { params: tipo ? { tipo } : {} }),
  createCategory: (data) => api.post('/financeiro/categories', data),
  updateCategory: (id, data) => api.put(`/financeiro/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/financeiro/categories/${id}`),
  // Services
  listServices: (params) => api.get('/financeiro/services', { params }),
  getService: (id) => api.get(`/financeiro/services/${id}`),
  createService: (data) => api.post('/financeiro/services', data),
  updateService: (id, data) => api.put(`/financeiro/services/${id}`, data),
  deleteService: (id) => api.delete(`/financeiro/services/${id}`),
  toggleParcelaPago: (svcId, parcelaId) => api.put(`/financeiro/services/${svcId}/parcelas/${parcelaId}/toggle-pago`),
  finalizar: (svcId, data) => api.put(`/financeiro/services/${svcId}/finalizar`, data || {}),
  cancelar: (svcId, data) => api.put(`/financeiro/services/${svcId}/cancelar`, data || {}),
  reabrir: (svcId) => api.put(`/financeiro/services/${svcId}/reabrir`),
  // Manejo
  atualizarManejo: (svcId, data) => api.put(`/financeiro/services/${svcId}/manejo`, data),
  transferirManejo: (svcId, data) => api.post(`/financeiro/services/${svcId}/manejo/transferir`, data),
  // Attachments
  uploadAttachment: (svcId, formData) => api.post(`/financeiro/services/${svcId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteAttachment: (svcId, attId) => api.delete(`/financeiro/services/${svcId}/attachments/${attId}`),
  // Expenses
  listExpenses: (params) => api.get('/financeiro/expenses', { params }),
  createExpense: (data) => api.post('/financeiro/expenses', data),
  updateExpense: (id, data) => api.put(`/financeiro/expenses/${id}`, data),
  deleteExpense: (id) => api.delete(`/financeiro/expenses/${id}`),
  // Receipt
  recibo: (svcId, parcelaId) => api.get(`/financeiro/services/${svcId}/recibo`, { params: parcelaId ? { parcela_id: parcelaId } : {} }),
  // Relatório
  listResponsaveis: () => api.get('/financeiro/responsaveis'),
  relatorio: (params) => api.get('/financeiro/relatorio', { params }),
};

export default api;
